import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { FiEdit2, FiTrash2, FiPlus, FiSearch, FiSettings, FiDatabase } from 'react-icons/fi';
import Portal from '../Portal';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const KpiConfiguration = () => {
  const [kpiConfigs, setKpiConfigs] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddKpi, setShowAddKpi] = useState(false);
  const [editingKpi, setEditingKpi] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [kpiResponse, endpointsResponse] = await Promise.all([
        adminAPI.getKpiConfigs(),
        adminAPI.getEndpoints()
      ]);
      setKpiConfigs(kpiResponse.data.kpiConfigs);
      setEndpoints(endpointsResponse.data.endpoints);
    } catch (error) {
      showMessage('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleDeleteKpi = async (kpiId) => {
    if (!window.confirm('Are you sure you want to delete this KPI configuration? This action cannot be undone.')) {
      return;
    }

    try {
      await adminAPI.deleteKpiConfig(kpiId);
      setKpiConfigs(kpiConfigs.filter(kpi => kpi.id !== kpiId));
      showMessage('KPI configuration deleted successfully', 'success');
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to delete KPI configuration', 'error');
    }
  };

  const filteredKpis = kpiConfigs.filter(kpi =>
    kpi.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kpi.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kpi.endpointName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading KPI configurations...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>KPI Configuration</h1>
        <button
          onClick={() => setShowAddKpi(true)}
          className="btn btn-primary"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <FiPlus style={{ marginRight: '5px' }} />
          Add KPI Config
        </button>
      </div>

      {message && (
        <div 
          className={messageType === 'error' ? 'error-message' : 'success-message'}
          style={{ marginBottom: '20px' }}
        >
          {message}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title">KPI Configurations ({filteredKpis.length})</h2>
            <div style={{ position: 'relative', width: '300px' }}>
              <FiSearch style={{ 
                position: 'absolute', 
                left: '10px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#666'
              }} />
              <input
                type="text"
                placeholder="Search KPI configurations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                style={{ paddingLeft: '35px' }}
              />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Endpoint</th>
                <th>Data Path</th>
                <th>Display Type</th>
                <th>Units</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredKpis.map(kpi => (
                <tr key={kpi.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <FiSettings style={{ marginRight: '8px', color: '#007bff' }} />
                      <strong>{kpi.name}</strong>
                    </div>
                  </td>
                  <td>{kpi.description || 'No description'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <FiDatabase style={{ marginRight: '5px', color: '#6c757d' }} />
                      {kpi.endpointName || `Endpoint ${kpi.endpointId}`}
                    </div>
                  </td>
                  <td>
                    <code style={{ 
                      backgroundColor: '--bg-primary', 
                      padding: '2px 6px', 
                      borderRadius: '3px',
                      fontSize: '12px'
                    }}>
                      {kpi.dataPath}
                    </code>
                  </td>
                  <td>
                    <span className={`badge ${getDisplayTypeBadgeClass(kpi.displayType)}`}>
                      {kpi.displayType.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td>{kpi.units || '-'}</td>
                  <td>
                    <span 
                      className={`badge ${kpi.isActive ? 'badge-success' : 'badge-secondary'}`}
                    >
                      {kpi.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => setEditingKpi(kpi)}
                        className="btn btn-small btn-secondary"
                        title="Edit KPI Configuration"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        onClick={() => handleDeleteKpi(kpi.id)}
                        className="btn btn-small btn-danger"
                        title="Delete KPI Configuration"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredKpis.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {searchTerm ? 'No KPI configurations found matching your search.' : 'No KPI configurations found.'}
              {!searchTerm && (
                <div style={{ marginTop: '10px' }}>
                  <button
                    onClick={() => setShowAddKpi(true)}
                    className="btn btn-primary"
                  >
                    Create Your First KPI Configuration
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showAddKpi && (
        <Portal>
          <KpiConfigModal
            endpoints={endpoints}
            onClose={() => setShowAddKpi(false)}
            onKpiAdded={(newKpi) => {
              setKpiConfigs([...kpiConfigs, newKpi]);
              setShowAddKpi(false);
              showMessage('KPI configuration created successfully', 'success');
            }}
            onError={(error) => showMessage(error, 'error')}
          />
        </Portal>
      )}

      {editingKpi && (
        <Portal>
          <KpiConfigModal
            kpi={editingKpi}
            endpoints={endpoints}
            onClose={() => setEditingKpi(null)}
            onKpiUpdated={(updatedKpi) => {
              setKpiConfigs(kpiConfigs.map(kpi => 
                kpi.id === updatedKpi.id ? updatedKpi : kpi
              ));
              setEditingKpi(null);
              showMessage('KPI configuration updated successfully', 'success');
            }}
            onError={(error) => showMessage(error, 'error')}
          />
        </Portal>
      )}
    </div>
  );
};

const getDisplayTypeBadgeClass = (displayType) => {
  switch (displayType) {
    case 'number': return 'badge-primary';
    case 'progress': return 'badge-info';
    case 'line_chart': return 'badge-success';
    case 'bar_chart': return 'badge-warning';
    case 'area_chart': return 'badge-secondary';
    default: return 'badge-secondary';
  }
};

const KpiConfigModal = ({ kpi, endpoints, onClose, onKpiAdded, onKpiUpdated, onError }) => {
  const [formData, setFormData] = useState({
    name: kpi?.name || '',
    description: kpi?.description || '',
    endpointId: kpi?.endpointId || '',
    dataPath: kpi?.dataPath || '',
    displayType: kpi?.displayType || 'number',
    units: kpi?.units || '',
    refreshInterval: kpi?.refreshInterval || 300,
    thresholds: kpi?.thresholds ? JSON.stringify(kpi.thresholds, null, 2) : '',
    chartConfig: kpi?.chartConfig ? JSON.stringify(kpi.chartConfig, null, 2) : '',
    chartLabelsPath: kpi?.chartLabelsPath || '',
    chartDataPath: kpi?.chartDataPath || '',
    chartDatasetLabel: kpi?.chartDatasetLabel || '',
    chartColor: kpi?.chartColor || '#007bff'
  });
  const [multiSeriesEnabled, setMultiSeriesEnabled] = useState(() => {
    // Debug logging to see what we're receiving
    console.log('🔍 MULTI-SERIES DEBUG: Initializing multiSeriesEnabled');
    console.log('🔍 MULTI-SERIES DEBUG: kpi object:', kpi);
    console.log('🔍 MULTI-SERIES DEBUG: kpi.chartDataSeries:', kpi?.chartDataSeries);
    console.log('🔍 MULTI-SERIES DEBUG: chartDataSeries type:', typeof kpi?.chartDataSeries);
    
    if (!kpi?.chartDataSeries) {
      console.log('🔍 MULTI-SERIES DEBUG: No chartDataSeries found, defaulting to false');
      return false;
    }
    
    let parsedSeries = null;
    try {
      if (typeof kpi.chartDataSeries === 'string') {
        parsedSeries = JSON.parse(kpi.chartDataSeries);
      } else if (Array.isArray(kpi.chartDataSeries)) {
        parsedSeries = kpi.chartDataSeries;
      }
    } catch (error) {
      console.warn('🔍 MULTI-SERIES DEBUG: Failed to parse chartDataSeries:', error);
      return false;
    }
    
    // Check if it's a non-empty array with at least one series that has meaningful data
    const isValidMultiSeries = Array.isArray(parsedSeries) && 
                               parsedSeries.length > 0 && 
                               parsedSeries.some(series => series.label || series.path);
    
    console.log('🔍 MULTI-SERIES DEBUG: parsedSeries:', parsedSeries);
    console.log('🔍 MULTI-SERIES DEBUG: isValidMultiSeries:', isValidMultiSeries);
    
    return isValidMultiSeries;
  });
  
  const [seriesConfig, setSeriesConfig] = useState(() => {
    console.log('🔍 MULTI-SERIES DEBUG: Initializing seriesConfig');
    
    if (kpi?.chartDataSeries) {
      try {
        // Handle both parsed object (from backend fix) and string (legacy)
        if (typeof kpi.chartDataSeries === 'string') {
          const parsed = JSON.parse(kpi.chartDataSeries);
          console.log('🔍 MULTI-SERIES DEBUG: Parsed from string:', parsed);
          return Array.isArray(parsed) && parsed.length > 0 ? parsed : [{ label: '', path: '', color: '#007bff' }];
        } else if (Array.isArray(kpi.chartDataSeries)) {
          console.log('🔍 MULTI-SERIES DEBUG: Using array directly:', kpi.chartDataSeries);
          return kpi.chartDataSeries.length > 0 ? kpi.chartDataSeries : [{ label: '', path: '', color: '#007bff' }];
        }
      } catch (error) {
        console.warn('🔍 MULTI-SERIES DEBUG: Failed to parse chartDataSeries:', error);
      }
    }
    
    console.log('🔍 MULTI-SERIES DEBUG: Using default seriesConfig');
    return [{ label: '', path: '', color: '#007bff' }];
  });
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const displayTypes = [
    { value: 'number', label: 'Number' },
    { value: 'progress', label: 'Progress Bar' },
    { value: 'line_chart', label: 'Line Chart' },
    { value: 'bar_chart', label: 'Bar Chart' },
    { value: 'area_chart', label: 'Area Chart' },
    { value: 'pie_chart', label: 'Pie Chart' }
  ];

  const isChartType = ['line_chart', 'bar_chart', 'area_chart', 'pie_chart'].includes(formData.displayType);

  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewData(null);
    setPreviewError(null);

    try {
      // Validate required fields based on display type
      if (!formData.endpointId) {
        throw new Error('Endpoint is required for preview');
      }

      if (isChartType) {
        // For chart types, validate chart-specific paths
        if (!formData.chartLabelsPath || !formData.chartDataPath) {
          throw new Error('Chart labels path and chart data path are required for chart preview');
        }

        // Get the raw API response first
        const response = await adminAPI.testKpiConfig({
          endpointId: formData.endpointId,
          dataPath: formData.chartLabelsPath, // Use labels path for initial test
          chartLabelsPath: formData.chartLabelsPath,
          chartDataPath: formData.chartDataPath,
          displayType: formData.displayType
        });
        
        if (response.data.success) {
          // Check if multi-series is enabled and extract accordingly
          let chartData;
          if (multiSeriesEnabled && seriesConfig.length > 0) {
            // Extract multi-series chart data
            chartData = extractMultiSeriesChartData(
              response.data.data, 
              formData.chartLabelsPath, 
              seriesConfig
            );
          } else {
            // Extract single-series chart data
            chartData = extractChartData(
              response.data.data, 
              formData.chartLabelsPath, 
              formData.chartDataPath,
              formData.chartDatasetLabel || 'Data',
              formData.chartColor
            );
          }

          setPreviewData({
            rawResponse: response.data.data,
            chartData: chartData,
            formattedValue: `Chart Preview: ${formData.displayType.replace('_', ' ').toUpperCase()}`,
            dataPath: multiSeriesEnabled ? 
              `Labels: ${formData.chartLabelsPath}, Series: ${seriesConfig.length} configured` :
              `Labels: ${formData.chartLabelsPath}, Data: ${formData.chartDataPath}`,
            pathType: 'chart',
            isChart: true,
            displayType: formData.displayType,
            isMultiSeries: multiSeriesEnabled
          });
        } else {
          throw new Error(response.data.error || 'Failed to get chart data from endpoint');
        }
      } else {
        // For single-value types, validate dataPath
        if (!formData.dataPath) {
          throw new Error('Data path is required for single-value KPI preview');
        }

        // Use the backend's test-kpi-config route which supports both JSONPath and dot notation
        const response = await adminAPI.testKpiConfig({
          endpointId: formData.endpointId,
          dataPath: formData.dataPath
        });
        
        if (response.data.success) {
          setPreviewData({
            rawResponse: response.data.data,
            extractedValue: response.data.extractedValue,
            formattedValue: formatValue(response.data.extractedValue, formData.units),
            dataPath: formData.dataPath,
            pathType: response.data.pathType || 'unknown',
            isChart: false
          });
        } else {
          throw new Error(response.data.error || 'Failed to get data from endpoint');
        }
      }
    } catch (error) {
      setPreviewError(error.response?.data?.error || error.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  // Helper function to extract chart data from API response
  const extractChartData = (apiResponse, labelsPath, dataPath, datasetLabel, color) => {
    try {
      // Extract labels and data using JSONPath or dot notation
      const labels = extractValueFromPath(apiResponse, labelsPath);
      const data = extractValueFromPath(apiResponse, dataPath);

      // Validate that both are arrays
      if (!Array.isArray(labels) || !Array.isArray(data)) {
        throw new Error('Chart labels and data must be arrays');
      }

      if (labels.length !== data.length) {
        throw new Error('Chart labels and data arrays must have the same length');
      }

      return {
        labels: labels,
        datasets: [{
          label: datasetLabel,
          data: data,
          backgroundColor: color + '20', // Add transparency for area/bar charts
          borderColor: color,
          borderWidth: 2,
          fill: formData.displayType === 'area_chart'
        }]
      };
    } catch (error) {
      throw new Error(`Failed to extract chart data: ${error.message}`);
    }
  };

  // Helper function to extract multi-series chart data from API response
  const extractMultiSeriesChartData = (apiResponse, labelsPath, seriesConfig) => {
    try {
      // Extract labels using JSONPath or dot notation
      const labels = extractValueFromPath(apiResponse, labelsPath);

      // Validate that labels is an array
      if (!Array.isArray(labels)) {
        throw new Error('Chart labels must be an array');
      }

      // Extract data for each series
      const datasets = seriesConfig.map((series, index) => {
        if (!series.path || !series.label) {
          throw new Error(`Series ${index + 1} is missing required path or label`);
        }

        const data = extractValueFromPath(apiResponse, series.path);

        // Validate that data is an array
        if (!Array.isArray(data)) {
          throw new Error(`Data for series "${series.label}" must be an array`);
        }

        // Validate that data length matches labels length
        if (data.length !== labels.length) {
          throw new Error(`Data length for series "${series.label}" (${data.length}) must match labels length (${labels.length})`);
        }

        return {
          label: series.label,
          data: data,
          backgroundColor: series.color + '20', // Add transparency for area/bar charts
          borderColor: series.color,
          borderWidth: 2,
          fill: formData.displayType === 'area_chart'
        };
      });

      return {
        labels: labels,
        datasets: datasets
      };
    } catch (error) {
      throw new Error(`Failed to extract multi-series chart data: ${error.message}`);
    }
  };

  // Helper function to extract value from JSONPath or dot notation
  const extractValueFromPath = (obj, path) => {
    if (!path) return null;
    
    try {
      // Handle JSONPath (starts with $)
      if (path.startsWith('$')) {
        // Simple JSONPath implementation for basic cases
        const cleanPath = path.substring(2); // Remove '$.'
        return cleanPath.split('.').reduce((current, key) => {
          return current && current[key] !== undefined ? current[key] : null;
        }, obj);
      } else {
        // Handle dot notation
        return path.split('.').reduce((current, key) => {
          return current && current[key] !== undefined ? current[key] : null;
        }, obj);
      }
    } catch (error) {
      return null;
    }
  };


  // Helper function to format value with units
  const formatValue = (value, units) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Format numbers with commas
    if (typeof value === 'number' || !isNaN(value)) {
      const numValue = parseFloat(value);
      const formatted = numValue.toLocaleString();
      return units ? `${formatted} ${units}` : formatted;
    }
    
    return units ? `${value} ${units}` : value.toString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields based on display type
      if (isChartType) {
        if (!formData.chartLabelsPath || !formData.chartDataPath) {
          throw new Error('Chart labels path and chart data path are required for chart display types');
        }
      } else {
        if (!formData.dataPath) {
          throw new Error('Data path is required for single-value display types');
        }
      }

      let thresholds = null;
      let chartConfig = null;

      // Parse JSON fields if provided
      if (formData.thresholds.trim()) {
        try {
          thresholds = JSON.parse(formData.thresholds);
        } catch (error) {
          throw new Error('Invalid JSON in thresholds field');
        }
      }

      if (formData.chartConfig.trim()) {
        try {
          chartConfig = JSON.parse(formData.chartConfig);
        } catch (error) {
          throw new Error('Invalid JSON in chart config field');
        }
      }

      const payload = {
        ...formData,
        refreshInterval: parseInt(formData.refreshInterval),
        thresholds,
        chartConfig,
        // For chart types, ensure dataPath is not required but chart paths are included
        dataPath: isChartType ? null : formData.dataPath,
        // Include multi-series configuration if enabled
        chartDataSeries: multiSeriesEnabled && isChartType ? JSON.stringify(seriesConfig) : null
      };

      if (kpi) {
        const response = await adminAPI.updateKpiConfig(kpi.id, payload);
        onKpiUpdated(response.data.kpiConfig || { ...kpi, ...payload });
      } else {
        const response = await adminAPI.createKpiConfig(payload);
        onKpiAdded(response.data.kpiConfig || { id: Date.now(), ...payload });
      }
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Failed to save KPI configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h3>{kpi ? 'Edit KPI Configuration' : 'Add New KPI Configuration'}</h3>
          <button onClick={onClose} className="btn btn-small btn-secondary">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="form-input"
              required
              placeholder="e.g., Sales Revenue"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="form-input"
              rows="2"
              placeholder="Brief description of this KPI"
            />
          </div>

          <div className="form-group">
            <label className="form-label">API Endpoint *</label>
            <select
              value={formData.endpointId}
              onChange={(e) => setFormData({...formData, endpointId: e.target.value})}
              className="form-input"
              required
            >
              <option value="">Select an endpoint</option>
              {endpoints.map(endpoint => (
                <option key={endpoint.id} value={endpoint.id}>
                  {endpoint.name} ({endpoint.method} {endpoint.url})
                </option>
              ))}
            </select>
          </div>

          {!isChartType && (
            <div className="form-group">
              <label className="form-label">Data Path *</label>
              <input
                type="text"
                value={formData.dataPath}
                onChange={(e) => setFormData({...formData, dataPath: e.target.value})}
                className="form-input"
                required={!isChartType}
                placeholder="e.g., data.revenue or response.metrics.total"
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                JSON path to extract the single value from API response
              </small>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label className="form-label">Display Type *</label>
              <select
                value={formData.displayType}
                onChange={(e) => setFormData({...formData, displayType: e.target.value})}
                className="form-input"
                required
              >
                {displayTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Units</label>
              <input
                type="text"
                value={formData.units}
                onChange={(e) => setFormData({...formData, units: e.target.value})}
                className="form-input"
                placeholder="e.g., $, %, users"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Refresh Interval (seconds)</label>
            <input
              type="number"
              value={formData.refreshInterval}
              onChange={(e) => setFormData({...formData, refreshInterval: e.target.value})}
              className="form-input"
              min="30"
              placeholder="300"
            />
          </div>

          {/* Chart Configuration Fields - Only show for chart display types */}
          {isChartType && (
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #dee2e6', 
              borderRadius: '5px', 
              padding: '20px',
              marginBottom: '20px'
            }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Chart Configuration</h4>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
                Configure how chart data is extracted from the API response. Charts require arrays of labels and corresponding data values.
              </p>

              <div className="form-group">
                <label className="form-label">Chart Labels Path *</label>
                <input
                  type="text"
                  value={formData.chartLabelsPath}
                  onChange={(e) => setFormData({...formData, chartLabelsPath: e.target.value})}
                  className="form-input"
                  required={isChartType}
                  placeholder="e.g., data.months or response.categories"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  JSONPath to extract chart labels (x-axis values) from API response
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Chart Data Path *</label>
                <input
                  type="text"
                  value={formData.chartDataPath}
                  onChange={(e) => setFormData({...formData, chartDataPath: e.target.value})}
                  className="form-input"
                  required={isChartType}
                  placeholder="e.g., data.values or response.sales"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  JSONPath to extract chart data values (y-axis values) from API response
                </small>
              </div>

              {/* Multi-Series Configuration Toggle */}
              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <input
                    type="checkbox"
                    id="multiSeriesEnabled"
                    checked={multiSeriesEnabled}
                    onChange={(e) => {
                      setMultiSeriesEnabled(e.target.checked);
                      if (!e.target.checked) {
                        // Reset to single series when disabled
                        setSeriesConfig([{ label: '', path: '', color: '#007bff' }]);
                      }
                    }}
                    style={{ marginRight: '10px' }}
                  />
                  <label htmlFor="multiSeriesEnabled" className="form-label" style={{ margin: 0, fontWeight: 'bold' }}>
                    Enable Multi-Series Chart
                  </label>
                </div>
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Enable this to display multiple data series in the same chart for comparison
                </small>
              </div>

              {multiSeriesEnabled ? (
                /* Multi-Series Configuration */
                <div style={{ 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffeaa7', 
                  borderRadius: '4px', 
                  padding: '15px',
                  marginBottom: '15px'
                }}>
                  <h5 style={{ margin: '0 0 15px 0', color: '#856404' }}>📊 Multi-Series Configuration</h5>
                  <p style={{ color: '#856404', fontSize: '14px', marginBottom: '15px' }}>
                    Configure multiple data series to display in the same chart. Each series can have its own data path and color.
                  </p>

                  {seriesConfig.map((series, index) => (
                    <div key={index} style={{ 
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      padding: '15px',
                      marginBottom: '10px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h6 style={{ margin: 0, color: '#333' }}>Series {index + 1}</h6>
                        {seriesConfig.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newConfig = seriesConfig.filter((_, i) => i !== index);
                              setSeriesConfig(newConfig);
                            }}
                            className="btn btn-small btn-danger"
                            title="Remove Series"
                          >
                            <FiTrash2 />
                          </button>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: '10px' }}>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontSize: '12px' }}>Series Label</label>
                          <input
                            type="text"
                            value={series.label}
                            onChange={(e) => {
                              const newConfig = [...seriesConfig];
                              newConfig[index].label = e.target.value;
                              setSeriesConfig(newConfig);
                            }}
                            className="form-input"
                            placeholder="e.g., Sales, Revenue"
                            style={{ fontSize: '14px', padding: '8px' }}
                          />
                        </div>

                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontSize: '12px' }}>Data Path</label>
                          <input
                            type="text"
                            value={series.path}
                            onChange={(e) => {
                              const newConfig = [...seriesConfig];
                              newConfig[index].path = e.target.value;
                              setSeriesConfig(newConfig);
                            }}
                            className="form-input"
                            placeholder="e.g., data.sales"
                            style={{ fontSize: '14px', padding: '8px' }}
                          />
                        </div>

                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontSize: '12px' }}>Color</label>
                          <input
                            type="color"
                            value={series.color}
                            onChange={(e) => {
                              const newConfig = [...seriesConfig];
                              newConfig[index].color = e.target.value;
                              setSeriesConfig(newConfig);
                            }}
                            className="form-input"
                            style={{ height: '32px', padding: '2px' }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => {
                      const colors = ['#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8', '#6f42c1'];
                      const nextColor = colors[seriesConfig.length % colors.length];
                      setSeriesConfig([...seriesConfig, { label: '', path: '', color: nextColor }]);
                    }}
                    className="btn btn-small btn-secondary"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <FiPlus style={{ marginRight: '5px' }} />
                    Add Series
                  </button>

                  <div style={{ 
                    backgroundColor: '#d1ecf1', 
                    border: '1px solid #bee5eb', 
                    borderRadius: '4px', 
                    padding: '10px',
                    marginTop: '15px'
                  }}>
                    <strong style={{ color: '#0c5460' }}>💡 Multi-Series Requirements:</strong>
                    <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px', color: '#0c5460', fontSize: '12px' }}>
                      <li>All series must use the same labels path (x-axis)</li>
                      <li>Each series can have its own data path and color</li>
                      <li>Data arrays for all series must have the same length</li>
                      <li>Example: Sales data at "data.sales", Profit data at "data.profit"</li>
                    </ul>
                  </div>
                </div>
              ) : (
                /* Single Series Configuration */
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label className="form-label">Dataset Label</label>
                    <input
                      type="text"
                      value={formData.chartDatasetLabel}
                      onChange={(e) => setFormData({...formData, chartDatasetLabel: e.target.value})}
                      className="form-input"
                      placeholder="e.g., Sales, Revenue, Users"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Label for the data series in the chart legend
                    </small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Chart Color</label>
                    <input
                      type="color"
                      value={formData.chartColor}
                      onChange={(e) => setFormData({...formData, chartColor: e.target.value})}
                      className="form-input"
                      style={{ height: '40px' }}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Primary color for chart elements
                    </small>
                  </div>
                </div>
              )}

              <div style={{ 
                backgroundColor: '#e7f3ff', 
                border: '1px solid #b3d9ff', 
                borderRadius: '4px', 
                padding: '12px',
                marginTop: '15px'
              }}>
                <strong style={{ color: '#0066cc' }}>💡 Chart Data Requirements:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#0066cc' }}>
                  <li>Labels and data arrays must have the same length</li>
                  <li>For pie charts: labels represent categories, data represents values</li>
                  <li>For line/bar/area charts: labels are x-axis, data are y-axis values</li>
                  <li>Example API response: <code>{`{"months": ["Jan", "Feb"], "sales": [100, 150]}`}</code></li>
                </ul>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Thresholds (JSON)</label>
            <textarea
              value={formData.thresholds}
              onChange={(e) => setFormData({...formData, thresholds: e.target.value})}
              className="form-input"
              rows="3"
              placeholder='{"warning": 1000, "critical": 500}'
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Optional: Define warning/critical thresholds as JSON
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Chart Configuration (JSON)</label>
            <textarea
              value={formData.chartConfig}
              onChange={(e) => setFormData({...formData, chartConfig: e.target.value})}
              className="form-input"
              rows="3"
              placeholder='{"color": "#007bff", "showGrid": true}'
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Optional: Chart-specific configuration as JSON
            </small>
          </div>

          {/* KPI Preview Section */}
          <div className="form-group" style={{ borderTop: '1px solid #eee', paddingTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h4 style={{ margin: 0, color: '#333' }}>Preview KPI</h4>
              <button
                type="button"
                onClick={handlePreview}
                disabled={previewing || !formData.endpointId || (isChartType ? (!formData.chartLabelsPath || !formData.chartDataPath) : !formData.dataPath)}
                className="btn btn-info"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <FiSettings style={{ marginRight: '5px' }} />
                {previewing ? 'Loading Preview...' : 'Preview KPI'}
              </button>
            </div>

            {previewData && (
              <div style={{ 
                backgroundColor: '#d4edda', 
                border: '1px solid #c3e6cb', 
                borderRadius: '5px', 
                padding: '15px',
                marginBottom: '15px'
              }}>
                <h5 style={{ color: '#155724', margin: '0 0 15px 0' }}>✅ KPI Preview</h5>
                
                {/* KPI Card Preview */}
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '15px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h6 style={{ margin: 0, color: '#333' }}>{formData.name || 'KPI Name'}</h6>
                    <span className={`badge ${getDisplayTypeBadgeClass(formData.displayType)}`}>
                      {formData.displayType.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  
                  {/* Render actual chart for chart types */}
                  {previewData.isChart && previewData.chartData ? (
                    <div style={{ height: '300px', marginBottom: '10px' }}>
                      {previewData.displayType === 'line_chart' && (
                        <Line 
                          data={previewData.chartData} 
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'top',
                              },
                              title: {
                                display: true,
                                text: formData.name || 'Chart Preview'
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true
                              }
                            }
                          }} 
                        />
                      )}
                      {previewData.displayType === 'bar_chart' && (
                        <Bar 
                          data={previewData.chartData} 
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'top',
                              },
                              title: {
                                display: true,
                                text: formData.name || 'Chart Preview'
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true
                              }
                            }
                          }} 
                        />
                      )}
                      {previewData.displayType === 'area_chart' && (
                        <Line 
                          data={{
                            ...previewData.chartData,
                            datasets: previewData.chartData.datasets.map(dataset => ({
                              ...dataset,
                              fill: true,
                              backgroundColor: dataset.borderColor + '20'
                            }))
                          }} 
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'top',
                              },
                              title: {
                                display: true,
                                text: formData.name || 'Chart Preview'
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true
                              }
                            }
                          }} 
                        />
                      )}
                      {previewData.displayType === 'pie_chart' && (
                        <Pie 
                          data={{
                            ...previewData.chartData,
                            datasets: previewData.chartData.datasets.map(dataset => ({
                              ...dataset,
                              backgroundColor: previewData.chartData.labels.map((_, index) => {
                                const hue = (index * 137.508) % 360; // Golden angle approximation
                                return `hsl(${hue}, 70%, 60%)`;
                              }),
                              borderWidth: 1,
                              borderColor: '#fff'
                            }))
                          }} 
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'right',
                              },
                              title: {
                                display: true,
                                text: formData.name || 'Chart Preview'
                              }
                            }
                          }} 
                        />
                      )}
                    </div>
                  ) : (
                    // For non-chart types, show the formatted value
                    <div style={{ 
                      fontSize: '32px', 
                      fontWeight: '700', 
                      color: '#007bff',
                      marginBottom: '5px'
                    }}>
                      {previewData.formattedValue}
                    </div>
                  )}
                  
                  {formData.description && (
                    <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
                      {formData.description}
                    </p>
                  )}
                </div>

                {/* Data Extraction Details */}
                <div style={{ fontSize: '12px', color: '#155724' }}>
                  <strong>Data Path:</strong> <code>{previewData.dataPath}</code><br/>
                  <strong>Extracted Value:</strong> <code>{JSON.stringify(previewData.extractedValue)}</code><br/>
                  <strong>Formatted Value:</strong> <code>{previewData.formattedValue}</code>
                </div>
              </div>
            )}

            {previewError && (
              <div style={{ 
                backgroundColor: '#f8d7da', 
                border: '1px solid #f5c6cb', 
                borderRadius: '5px', 
                padding: '15px',
                marginBottom: '15px'
              }}>
                <h5 style={{ color: '#721c24', margin: '0 0 10px 0' }}>❌ Preview Failed</h5>
                <p style={{ color: '#721c24', margin: 0 }}>{previewError}</p>
              </div>
            )}

            {!previewData && !previewError && !previewing && (
              <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
                Preview your KPI configuration to see how it will look with real data from your API endpoint.
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Saving...' : (kpi ? 'Update KPI' : 'Create KPI')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KpiConfiguration;
